import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { inter, philosopher } from "@/lib/fonts";
import { Mic, SearchIcon, Store } from "lucide-react";
import React from "react";
import { SEARCH_DEBOUNCE_DELAY, SearchResponse } from "muminsmart-utils";
import { useLazyGetSearchResultsQuery } from "@/lib/api/apiSlice";
import CustomImage from "./Image";
import { Link } from "react-router-dom";
import { routes } from "@/lib/constants";
import ProductCard from "../product/ProductCard";

const initialResults: SearchResponse = {
  categories: [],
  subCategories: [],
  subSubCategories: [],
  products: [],
  collections: [],
  sellers: [],
};

interface SearchResultsContentProps {
  results: SearchResponse;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function SearchResultsContent({
  results,
  setOpen,
}: Readonly<SearchResultsContentProps>) {
  return (
    <div className="max-h-[55vh] space-y-5 overflow-y-auto px-1">
      {results.products.length ? (
        <section className="space-y-2">
          <h1
            className={`px-2 text-lg font-bold uppercase ${philosopher.className}`}
          >
            Products
          </h1>
          <div className="grid grid-cols-2 gap-3 px-2 sm:grid-cols-3 lg:grid-cols-4">
            {results.products.map((product) => (
              <ProductCard
                variant={product.variants[0]}
                product={product}
                key={product.id}
              />
            ))}
          </div>
        </section>
      ) : null}

      {results.categories.length ? (
        <section className="space-y-2 px-2">
          <h1
            className={`text-lg font-bold uppercase ${philosopher.className}`}
          >
            Categories
          </h1>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
            {results.categories.map((cat) => (
              <Link
                to={routes.categories.id(cat.id)}
                key={cat.id}
                onClick={() => setOpen(false)}
                className="flex flex-col items-center rounded-xl bg-white p-2"
              >
                <CustomImage
                  src={cat.image}
                  alt={cat.name}
                  className="w-full rounded-lg bg-linear-to-t from-20% from-[#F0E0C7] to-70% to-transparent"
                />
                <span
                  className={`mt-1 line-clamp-1 text-center text-sm text-[#6F6F6F] ${philosopher.className}`}
                >
                  {cat.name}
                </span>
              </Link>
            ))}
            {results.subCategories.map((subCategory) => (
              <Link
                to={routes.categories.subCategories.id(
                  subCategory.categoryId,
                  subCategory.id,
                )}
                key={subCategory.id}
                onClick={() => setOpen(false)}
                className="flex flex-col items-center rounded-xl bg-white p-2"
              >
                <CustomImage
                  src={subCategory.image}
                  alt={subCategory.name}
                  className="w-full rounded-lg bg-linear-to-t from-20% from-[#F0E0C7] to-70% to-transparent"
                />
                <span
                  className={`mt-1 line-clamp-1 text-center text-sm text-[#6F6F6F] ${inter.className}`}
                >
                  {subCategory.name}
                </span>
              </Link>
            ))}
            {results.subSubCategories.map((subSubCategory) => (
              <Link
                to={routes.categories.subCategories.subSubCategories.id(
                  subSubCategory.categoryId,
                  subSubCategory.subCategoryId,
                  subSubCategory.id,
                )}
                key={subSubCategory.id}
                onClick={() => setOpen(false)}
                className="flex flex-col items-center rounded-xl bg-white p-2"
              >
                <CustomImage
                  src={subSubCategory.image}
                  alt={subSubCategory.name}
                  className="w-full rounded-lg bg-linear-to-t from-20% from-[#F0E0C7] to-70% to-transparent"
                />
                <span
                  className={`mt-1 line-clamp-1 text-center text-sm text-[#6F6F6F] ${inter.className}`}
                >
                  {subSubCategory.name}
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {results.collections.length ? (
        <section className="space-y-2 px-2">
          <h1
            className={`text-lg font-bold uppercase ${philosopher.className}`}
          >
            Collections
          </h1>
          <div className="space-y-2">
            {results.collections.map((collection) => (
              <Link
                to={routes.categories.index}
                key={collection.id}
                onClick={() => setOpen(false)}
                className="block rounded-lg bg-muted/45 px-3 py-2 text-sm"
              >
                <p className="font-medium text-foreground">{collection.name}</p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {results.sellers.length ? (
        <section className="space-y-2 px-2 pb-2">
          <h1
            className={`text-lg font-bold uppercase ${philosopher.className}`}
          >
            Sellers
          </h1>
          <div className="space-y-2">
            {results.sellers.map((seller) => (
              <Link
                to={routes.seller.id(seller.id)}
                key={seller.id}
                onClick={() => setOpen(false)}
                className="block rounded-lg hover:bg-primary hover:text-white px-3 py-2 text-sm"
              >
                <p className="font-medium flex gap-2 items-center">
                  <Store size={20} />
                  {seller.name}
                </p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

interface SearchProps {
  iconOnly?: boolean;
}

const Search = ({ iconOnly = false }: SearchProps) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState<SearchResponse>(initialResults);
  const iconAnchorRef = React.useRef<HTMLButtonElement | null>(null);
  const inputAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [fetchSearchResults, { isFetching: isLoading }] =
    useLazyGetSearchResultsQuery();

  const openSearch = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleSearchPointerDown = React.useCallback(
    (event: React.PointerEvent) => {
      // Prevent the follow-up click from landing on content that shifts up
      // when the search bar becomes fixed.
      event.preventDefault();
      event.stopPropagation();
      openSearch();
    },
    [openSearch],
  );

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setSearch("");
      setResults(initialResults);
    }
  }, []);

  const keepSearchInputInteractive = React.useCallback(
    (event: { preventDefault: () => void; target: EventTarget | null }) => {
      const target = event.target;

      if (
        target instanceof Node &&
        (iconAnchorRef.current?.contains(target) ||
          inputAnchorRef.current?.contains(target))
      ) {
        event.preventDefault();
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!open) {
      return;
    }

    // Keep typing uninterrupted when the popover opens.
    globalThis.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [open]);

  React.useEffect(() => {
    const keyword = search.trim();

    if (!keyword) {
      setResults(initialResults);
      return;
    }

    let isCancelled = false;
    const timeoutId = globalThis.setTimeout(() => {
      void fetchSearchResults({ search: keyword, limit: 6 })
        .unwrap()
        .then((data) => {
          if (!isCancelled) {
            setResults(data);
          }
        })
        .catch(() => {
          if (!isCancelled) {
            setResults(initialResults);
          }
        });
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      isCancelled = true;
      globalThis.clearTimeout(timeoutId);
    };
  }, [fetchSearchResults, search]);

  const hasAnyResult =
    results.products.length > 0 ||
    results.categories.length > 0 ||
    results.subCategories.length > 0 ||
    results.subSubCategories.length > 0 ||
    results.collections.length > 0 ||
    results.sellers.length > 0;

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <p className="px-2 py-3 text-sm text-muted-foreground">Searching...</p>
    );
  } else if (!search.trim()) {
    content = (
      <p className="px-2 py-3 text-sm text-muted-foreground">
        Start typing to search categories, products, collections, and sellers.
      </p>
    );
  } else if (!hasAnyResult) {
    content = (
      <p className="px-2 py-3 text-sm text-muted-foreground">
        No results found.
      </p>
    );
  } else {
    content = <SearchResultsContent results={results} setOpen={setOpen} />;
  }

  return (
    <>
      {open && !iconOnly ? (
        <div className="px-4 pb-4" aria-hidden="true">
          <div className="h-10 w-full" />
        </div>
      ) : null}

      <Popover open={open} onOpenChange={handleOpenChange}>
        {iconOnly && !open ? (
          <PopoverAnchor asChild>
            <button
              type="button"
              aria-label="Open search"
              ref={iconAnchorRef}
              className="rounded-full bg-white p-2 cursor-pointer shadow-md"
              onPointerDown={handleSearchPointerDown}
            >
              <SearchIcon strokeWidth={1} />
            </button>
          </PopoverAnchor>
        ) : (
          <div
            className={
              open
                ? "fixed inset-x-0 top-[var(--safe-area-top)] z-50 bg-[#FDFAF7] px-4 pt-4 pb-3 backdrop-blur-sm"
                : "relative px-4 pb-4"
            }
          >
            <PopoverAnchor asChild>
              <div ref={inputAnchorRef} className="w-full">
                <InputGroup
                  className={`w-full rounded-full shadow-sm border-none bg-white ${inter.className} placeholder-[#7B7B7B]`}
                >
                  <InputGroupAddon align="inline-start">
                    <SearchIcon className="h-4 w-4 text-gray-400" />
                  </InputGroupAddon>

                  <InputGroupInput
                    ref={inputRef}
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onPointerDown={handleSearchPointerDown}
                    onFocus={openSearch}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  {/* <InputGroupAddon align="inline-end">
                  <Mic className="h-4 w-4 text-gray-400" />
                </InputGroupAddon> */}
                </InputGroup>
              </div>
            </PopoverAnchor>
          </div>
        )}

        <PopoverContent
          align={iconOnly && !open ? "end" : "start"}
          side="bottom"
          sideOffset={10}
          className="w-screen border-none max-w-none p-3 space-y-4 bg-white"
          onOpenAutoFocus={(event: Event) => event.preventDefault()}
          onInteractOutside={keepSearchInputInteractive}
          onPointerDownOutside={keepSearchInputInteractive}
        >
          {content}
        </PopoverContent>
      </Popover>
    </>
  );
};

export default Search;
